/**
 * Source tab — read-mostly LaTeX of the current equation. Updates live as
 * tokens change. Syntax colours map to the app's theme-aware --lilia-code-*
 * tokens (Claude Design 2026-06-14, §6.3) so the source view themes with the
 * rest of the app's code surfaces:
 *
 *   \command       --lilia-code-command
 *   \begin / \end  --lilia-code-tag (bold)
 *   \label{...}    --lilia-code-comment (bold)
 *   { } braces     --lilia-code-brace
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { equationToLatex, wrappedSource, type Token } from "./tokens";
import { api } from "@/lib/api";
import { IconSparkles } from "@tabler/icons-react";

interface SourceTabProps {
  tokens: Token[];
  label?: string;
  /** Apply raw LaTeX back into the editor's token model (live round-trip). */
  onApplyLatex?: (latex: string) => void;
}


export function SourceTab({ tokens, label, onApplyLatex }: SourceTabProps) {
  const body = useMemo(() => equationToLatex(tokens), [tokens]);
  const wrapped = useMemo(() => wrappedSource(tokens, label), [tokens, label]);
  const [copied, setCopied] = useState(false);

  // Editable raw-LaTeX draft. Re-synced from the token-derived LaTeX whenever
  // the user isn't actively typing (e.g. after a button insert), so the buttons
  // and the source view stay in lockstep without fighting the caret.
  const [draft, setDraft] = useState(body);
  const editingRef = useRef(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => { if (!editingRef.current) setDraft(body); }, [body]);

  const push = useCallback((latex: string) => { onApplyLatex?.(latex); }, [onApplyLatex]);

  const onEdit = (v: string) => {
    setDraft(v);
    editingRef.current = true;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => push(v), 350);
  };

  // AI: natural language → LaTeX, fed straight into the editor.
  const [desc, setDesc] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const generate = useCallback(async () => {
    const d = desc.trim();
    if (!d || aiLoading) return;
    setAiLoading(true); setAiError(null);
    try {
      const res = await api.ai.suggestEquation(d);
      if (res?.latex) { editingRef.current = false; setDraft(res.latex); push(res.latex); }
      else setAiError("No equation returned — try rephrasing.");
    } catch { setAiError("Couldn't generate — try again."); }
    finally { setAiLoading(false); }
  }, [desc, aiLoading, push]);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(wrapped);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch {
      /* clipboard may be unavailable in iframes etc — silent fail */
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      {/* AI box — describe the math, get LaTeX into the editor */}
      <div style={{
        display: "flex", gap: 8, alignItems: "center",
        padding: "10px 12px", borderBottom: "1px solid var(--lilia-border)", background: "var(--lilia-surface)",
      }}>
        <IconSparkles size={15} style={{ color: "var(--lilia-accent, #6366f1)", flexShrink: 0 }} />
        <input
          data-testid="math-ai-input"
          value={desc}
          onChange={(e) => setDesc(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void generate(); } }}
          placeholder="Describe the math — e.g. “gaussian integral from 0 to ∞”"
          style={{
            flex: 1, minWidth: 0, border: "1px solid var(--lilia-border)", borderRadius: 6,
            padding: "5px 9px", fontSize: 12.5, background: "var(--lilia-bg)", color: "var(--lilia-text)", outline: "none",
          }}
        />
        <button
          type="button"
          data-testid="math-ai-generate"
          onClick={() => void generate()}
          disabled={aiLoading || !desc.trim()}
          style={{
            flexShrink: 0, padding: "5px 12px", borderRadius: 6, border: "none",
            background: "var(--lilia-accent, #6366f1)", color: "#fff", fontSize: 12, fontWeight: 600,
            cursor: aiLoading || !desc.trim() ? "default" : "pointer", opacity: aiLoading || !desc.trim() ? 0.55 : 1,
          }}
        >
          {aiLoading ? "…" : "Generate"}
        </button>
      </div>
      {aiError && (
        <div style={{ padding: "6px 12px", fontSize: 11.5, color: "var(--lilia-danger, #dc2626)", background: "var(--lilia-surface)" }}>
          {aiError}
        </div>
      )}

      {/* Header */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "8px 12px", borderBottom: "1px solid var(--lilia-border)", background: "var(--lilia-surface)",
      }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: "var(--lilia-text)" }}>
          LaTeX <span style={{ color: "var(--lilia-text-muted)", fontWeight: 400, marginLeft: 6 }}>· editable · {draft.length} chars</span>
        </span>
        <button
          type="button"
          onClick={handleCopy}
          style={{
            padding: "4px 10px", border: "1px solid var(--lilia-border)", borderRadius: 4,
            background: copied ? "color-mix(in srgb, var(--lilia-success) 12%, transparent)" : "var(--lilia-bg-muted)",
            color: copied ? "var(--lilia-success)" : "var(--lilia-text)", fontSize: 11, fontWeight: 600, cursor: "pointer",
          }}
        >
          {copied ? "Copied" : "Copy"}
        </button>
      </div>

      {/* Editable raw LaTeX — typing here updates the tokens + preview + buttons */}
      <textarea
        data-testid="math-source-tab"
        data-latex={draft}
        value={draft}
        onChange={(e) => onEdit(e.target.value)}
        onFocus={() => { editingRef.current = true; }}
        onBlur={() => { editingRef.current = false; push(draft); }}
        spellCheck={false}
        placeholder={"Type LaTeX here — e.g.  c^2 = a^2 + b^2"}
        style={{
          flex: 1, minHeight: 0, resize: "none", border: "none", outline: "none",
          padding: "12px", background: "var(--lilia-code-bg)", color: "var(--lilia-text)",
          fontFamily: "var(--lilia-font-mono, ui-monospace, monospace)", fontSize: 13, lineHeight: 1.7,
        }}
      />

      {/* Footer */}
      <div style={{
        borderTop: "1px solid var(--lilia-border)", background: "var(--lilia-surface)",
        padding: "6px 12px", display: "flex", alignItems: "center", gap: 10, fontSize: 11, color: "var(--lilia-text-secondary)",
      }}>
        <span style={{ color: "var(--lilia-text-muted)" }}>Edits sync to the buttons + preview live.</span>
      </div>
    </div>
  );
}
